import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Users,
  Wrench,
  Building2,
  Crown,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  DollarSign,
  Handshake,
  Zap,
  Globe,
  Star,
  ChevronRight
} from 'lucide-react';
import '../styles/PartnersSection.css';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.6, staggerChildren: 0.15 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const partnerTypes = [
  {
    id: 'referral',
    badge: 'Modelo 1',
    icon: <Handshake size={28} />,
    title: 'Referral Partner',
    tagline: 'Generá ingresos con tus contactos',
    idealFor: 'Freelancers, consultores o cualquier persona con una red de contactos que quieran monetizar sus leads sin complejidad operativa.',
    description:
      'El modelo más directo: llevás el lead, participás del cierre y te llevás tu comisión. Nosotros te apoyamos en todo el proceso.',
    benefits: [
      'Vos generás el lead y cerrás la venta',
      'Soporte de Runamatic durante el proceso comercial',
      'Sin mínimos ni compromisos contractuales',
      'Pagos puntuales por cada venta cerrada'
    ],
    earnings: {
      label: 'Comisión por venta',
      value: '10%',
      detail: 'de cada plan vendido'
    },
    accentClass: 'accent-referral'
  },
  {
    id: 'implementador',
    badge: 'Modelo 2',
    icon: <Wrench size={28} />,
    title: 'Partner Implementador',
    tagline: 'Vendé, configurá y cobrá más',
    idealFor: 'Perfiles técnicos, consultores de tecnología o agencias pequeñas que quieren ampliar su cartera de servicios con automatización de ventas.',
    description:
      'Vendés Runamatic a tus clientes, gestionás sus cuentas y además facturás el servicio de implementación a tu propio precio. Doble fuente de ingresos.',
    benefits: [
      'Comisión por cada venta cerrada',
      'Definís el precio de la implementación vos mismo',
      'Gestión directa de las cuentas de tus clientes',
      'Acceso a material de ventas y capacitación'
    ],
    earnings: {
      label: 'Ganás',
      value: '10% + 100%',
      detail: 'comisión + fee de implementación'
    },
    accentClass: 'accent-implementador'
  },
  {
    id: 'reseller',
    badge: 'Modelo 3',
    icon: <Building2 size={28} />,
    title: 'Reseller / Agencia',
    tagline: 'Integrá Runamatic a tu oferta',
    idealFor: 'Agencias de marketing, CRM, publicidad o tecnología que buscan ampliar su catálogo de servicios con una solución de automatización inteligente.',
    description:
      'Revendés nuestros planes como parte de tu oferta. Podés empaquetarlos junto a tus servicios actuales, definir tu propio pricing y acceder a descuentos por volumen.',
    benefits: [
      'Control total sobre el pricing y propuesta comercial',
      'Descuentos por volumen de cuentas gestionadas',
      'Ideal para integrar con servicios de marketing o CRM',
      'Soporte técnico prioritario para agencias'
    ],
    earnings: {
      label: 'Modelo de negocio',
      value: 'Markup libre',
      detail: 'vos definís el precio de reventa'
    },
    accentClass: 'accent-reseller'
  },
  {
    id: 'whitelabel',
    badge: 'Modelo 4',
    icon: <Crown size={28} />,
    title: 'Etiqueta Blanca',
    tagline: 'Tu marca, nuestra tecnología',
    idealFor: 'Agencias consolidadas o empresas que quieren escalar ofreciendo automatización de ventas con su propia identidad de marca, dominio y acceso a API.',
    description:
      'Operá la plataforma completa de Runamatic bajo tu propia marca y dominio. Tus clientes nunca sabrán que usás Runamatic — vos controlás el precio, la propuesta y la experiencia.',
    benefits: [
      'Plataforma operando bajo tu marca y dominio propio',
      'Hasta 50 cuentas tipo negocio incluidas',
      'Miembros de equipo, canales y mensajes ilimitados por cuenta',
      'Hasta 500.000 contactos compartidos entre todas las cuentas',
      'Hasta 20 cuentas DEMO sin costo de facturación',
      'Soporte prioritario y configuración en hasta 5 días',
      'Acceso a la API de la plataforma desde tu subdominio'
    ],
    pricing: [
      { month: 'Mes 1', price: 'USD 500' },
      { month: 'Mes 2', price: 'USD 800' },
      { month: 'Mes 3', price: 'USD 1.000' },
      { month: 'Mes 4+', price: 'USD 1.200' }
    ],
    extraAccount: 'USD 20 por cada cuenta adicional a partir de las 50',
    docsUrl: 'https://runamatic.io/docs/etiqueta-blanca-runamatic',
    accentClass: 'accent-whitelabel'
  }
];

const stats = [
  { icon: <DollarSign size={22} />, value: '4 modelos', label: 'de partnership disponibles' },
  { icon: <TrendingUp size={22} />, value: '10%', label: 'de comisión por venta' },
  { icon: <Globe size={22} />, value: '100%', label: 'del fee de implementación' },
  { icon: <Zap size={22} />, value: 'Sin techo', label: 'en ingresos recurrentes' }
];

const PartnersSection = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [statsRef, statsInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [ctaRef, ctaInView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="partners-section">
      {/* Background decorative shapes */}
      <div className="partners-bg">
        <div className="partners-bg-shape shape-1" />
        <div className="partners-bg-shape shape-2" />
        <div className="partners-bg-shape shape-3" />
      </div>

      <div className="partners-container">
        {/* ── Hero ─────────────────────────────────────────── */}
        <motion.div
          className="partners-hero"
          ref={heroRef}
          initial="hidden"
          animate={heroInView ? 'visible' : 'hidden'}
          variants={containerVariants}
        >
          <motion.div className="partners-eyebrow" variants={itemVariants}>
            <Star size={14} />
            <span>Programa de Partners</span>
          </motion.div>

          <motion.h1 className="partners-hero-title" variants={itemVariants}>
            Creá una nueva línea de negocio{' '}
            <span className="partners-gradient-text">con Runamatic</span>
          </motion.h1>

          <motion.p className="partners-hero-subtitle" variants={itemVariants}>
            Ofrecé automatización de ventas con IA en WhatsApp, Instagram y Web a tus clientes.
            Elegí el modelo que mejor se adapta a tu perfil y empezá a generar ingresos recurrentes
            sobre nuestra plataforma.
          </motion.p>

          <motion.div className="partners-hero-cta" variants={itemVariants}>
            <motion.a
              href="https://wa.me/5491123428342?text=Hola%2C%20quiero%20mas%20informaci%C3%B3n%20sobre%20Runamatic"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary partners-btn-hero"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
            >
              Quiero ser Partner
              <ArrowRight size={18} />
            </motion.a>
            <motion.a
              href="https://app.runamatic.io/en/login"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary partners-btn-hero"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
            >
              Conocer la plataforma
            </motion.a>
          </motion.div>
        </motion.div>

        {/* ── Stats ────────────────────────────────────────── */}
        <motion.div
          className="partners-stats"
          ref={statsRef}
          initial="hidden"
          animate={statsInView ? 'visible' : 'hidden'}
          variants={containerVariants}
        >
          {stats.map((s, i) => (
            <motion.div key={i} className="partners-stat-item" variants={itemVariants}>
              <div className="partners-stat-icon">{s.icon}</div>
              <div className="partners-stat-value">{s.value}</div>
              <div className="partners-stat-label">{s.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* ── Section title ─────────────────────────────────── */}
        <motion.div
          className="partners-models-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="partners-models-title">Elegí tu modelo de partnership</h2>
          <p className="partners-models-subtitle">
            Cuatro formas distintas de generar ingresos. Encontrá la que mejor se adapta
            a tu perfil, tus clientes y tu nivel de implicación.
          </p>
        </motion.div>

        {/* ── Partner cards ─────────────────────────────────── */}
        <div className="partners-grid">
          {partnerTypes.map((partner, index) => (
            <PartnerCard key={partner.id} partner={partner} index={index} />
          ))}
        </div>

        {/* ── CTA ──────────────────────────────────────────── */}
        <motion.div
          className="partners-cta-block"
          ref={ctaRef}
          initial="hidden"
          animate={ctaInView ? 'visible' : 'hidden'}
          variants={containerVariants}
        >
          <motion.div className="partners-cta-inner" variants={itemVariants}>
            <div className="partners-cta-icon">
              <Users size={32} />
            </div>
            <h2 className="partners-cta-title">¿Listo para sumarte?</h2>
            <p className="partners-cta-text">
              Escribinos por WhatsApp y un miembro del equipo te explicará en detalle cómo funciona
              el programa, qué modelo encaja mejor con tu perfil y cómo empezar.
            </p>
            <motion.a
              href="https://wa.me/5491123428342?text=Hola%2C%20quiero%20mas%20informaci%C3%B3n%20sobre%20Runamatic"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary partners-btn-cta"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
            >
              Contactar al equipo de Partners
              <ArrowRight size={18} />
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

const PartnerCard = ({ partner, index }) => {
  const [ref, inView] = useInView({ threshold: 0.08, triggerOnce: true });
  const isWhiteLabel = partner.id === 'whitelabel';

  return (
    <motion.div
      ref={ref}
      className={`partner-card ${partner.accentClass} ${isWhiteLabel ? 'partner-card-featured' : ''}`}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay: index * 0.08 }}
      whileHover={{ y: -6, transition: { duration: 0.25 } }}
    >
      {/* Card header */}
      <div className="partner-card-header">
        <div className="partner-card-top-row">
          <span className="partner-badge">{partner.badge}</span>
          {isWhiteLabel && (
            <span className="partner-featured-badge">
              <Star size={12} /> Premium
            </span>
          )}
        </div>
        <div className="partner-icon-wrap">{partner.icon}</div>
        <h3 className="partner-title">{partner.title}</h3>
        <p className="partner-tagline">{partner.tagline}</p>
      </div>

      {/* Card body */}
      <div className="partner-card-body">
        <div className="partner-ideal-block">
          <span className="partner-ideal-label">Ideal para:</span>
          <p className="partner-ideal-text">{partner.idealFor}</p>
        </div>

        <p className="partner-description">{partner.description}</p>

        <ul className="partner-benefits">
          {partner.benefits.map((b, i) => (
            <li key={i} className="partner-benefit-item">
              <CheckCircle size={16} className="partner-check-icon" />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        {/* Earnings / pricing */}
        {isWhiteLabel ? (
          <div className="partner-pricing-block">
            <div className="partner-pricing-title">Estructura de precios</div>
            <div className="partner-pricing-table">
              {partner.pricing.map((row, i) => (
                <div
                  key={i}
                  className={`partner-pricing-row ${i === partner.pricing.length - 1 ? 'pricing-row-highlight' : ''}`}
                >
                  <span className="pricing-month">{row.month}</span>
                  <span className="pricing-price">{row.price}</span>
                </div>
              ))}
            </div>
            <div className="partner-pricing-extra">
              <ChevronRight size={14} />
              {partner.extraAccount}
            </div>
          </div>
        ) : (
          <div className="partner-earnings-block">
            <div className="partner-earnings-label">{partner.earnings.label}</div>
            <div className="partner-earnings-value">{partner.earnings.value}</div>
            <div className="partner-earnings-detail">{partner.earnings.detail}</div>
          </div>
        )}
      </div>

      {/* Card footer */}
      <div className={`partner-card-footer ${isWhiteLabel ? 'partner-card-footer-wl' : ''}`}>
        <motion.a
          href="https://wa.me/5491123428342?text=Hola%2C%20quiero%20mas%20informaci%C3%B3n%20sobre%20Runamatic"
          target="_blank"
          rel="noopener noreferrer"
          className={`partner-card-cta ${isWhiteLabel ? 'btn btn-primary' : 'partner-cta-link'}`}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
        >
          Consultar por WhatsApp
          <ArrowRight size={16} />
        </motion.a>

        {partner.docsUrl && (
          <motion.a
            href={partner.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="partner-docs-link"
            whileHover={{ x: 4 }}
            transition={{ duration: 0.2 }}
          >
            Ver documentación completa
            <ArrowRight size={14} />
          </motion.a>
        )}
      </div>
    </motion.div>
  );
};

export default PartnersSection;
